import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'pt' | 'en' | 'es';
type Currency = 'BRL' | 'USD' | 'EUR';

interface LanguageContextType {
  language: Language;
  currency: Currency;
  setLanguage: (lang: Language) => void;
  setCurrency: (curr: Currency) => void;
  t: (key: string) => string;
  formatPrice: (price: number) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Traduções
const translations = {
  pt: {
    // Header
    'header.home': 'Início',
    'header.modules': 'Módulos',
    'header.about': 'Sobre',
    'header.contact': 'Contato',
    'header.solutions': 'Soluções',
    'header.login': 'Entrar',
    'header.start_free': 'Começar Gratuitamente',

    // Hero Section
    'hero.title': 'O único CRM que transforma',
    'hero.title_highlight': 'WhatsApp em máquina de vender imóveis',
    'hero.subtitle': 'Do primeiro "oi" ao contrato assinado - 100% no automático.',
    'hero.subtitle_highlight': '+180% mais vendas',
    'hero.subtitle_end': 'para corretores inteligentes.',
    'hero.cta_primary': 'ATIVAR MINHA MÁQUINA DE VENDAS',
    'hero.cta_secondary': 'Testar o Chatbot AGORA',

    // Platform Features
    'feature.whatsapp.title': 'WhatsApp Automático',
    'feature.whatsapp.desc':
      'Cada lead recebe follow-up personalizado 24/7 - sem você mexer um dedo',
    'feature.qualify.title': 'Qualifica Sozinho',
    'feature.qualify.desc': 'IA descobre orçamento, urgência e intenção real de cada interessado',
    'feature.commission.title': 'Mais Comissões',
    'feature.commission.desc':
      'Corretores faturam 3x mais com o mesmo esforço usando nosso sistema',

    // Common
    'common.free': 'GRATUITO',
    'common.popular': 'POPULAR',
    'common.premium': 'PREMIUM',
    'common.learn_more': 'Saiba Mais',
    'common.contact_sales': 'Falar com Vendas',
  },
  en: {
    // Header
    'header.home': 'Home',
    'header.modules': 'Modules',
    'header.about': 'About',
    'header.contact': 'Contact',
    'header.solutions': 'Solutions',
    'header.login': 'Login',
    'header.start_free': 'Start Free',

    // Hero Section
    'hero.title': 'The only CRM that transforms',
    'hero.title_highlight': 'WhatsApp into a real estate selling machine',
    'hero.subtitle': 'From first "hello" to signed contract - 100% automated.',
    'hero.subtitle_highlight': '+180% more sales',
    'hero.subtitle_end': 'for smart realtors.',
    'hero.cta_primary': 'ACTIVATE MY SALES MACHINE',
    'hero.cta_secondary': 'Test Chatbot NOW',

    // Platform Features
    'feature.whatsapp.title': 'WhatsApp Automation',
    'feature.whatsapp.desc':
      'Each lead receives personalized 24/7 follow-up - without you lifting a finger',
    'feature.qualify.title': 'Auto-Qualification',
    'feature.qualify.desc': 'AI discovers budget, urgency and real intention of each prospect',
    'feature.commission.title': 'More Commissions',
    'feature.commission.desc': 'Realtors earn 3x more with same effort using our system',

    // Common
    'common.free': 'FREE',
    'common.popular': 'POPULAR',
    'common.premium': 'PREMIUM',
    'common.learn_more': 'Learn More',
    'common.contact_sales': 'Contact Sales',
  },
  es: {
    // Header
    'header.home': 'Inicio',
    'header.modules': 'Módulos',
    'header.about': 'Acerca',
    'header.contact': 'Contacto',
    'header.solutions': 'Soluciones',
    'header.login': 'Iniciar Sesión',
    'header.start_free': 'Empezar Gratis',

    // Hero Section
    'hero.title': 'El único CRM que transforma',
    'hero.title_highlight': 'WhatsApp en máquina de vender inmuebles',
    'hero.subtitle': 'Desde el primer "hola" hasta el contrato firmado - 100% automático.',
    'hero.subtitle_highlight': '+180% más ventas',
    'hero.subtitle_end': 'para corredores inteligentes.',
    'hero.cta_primary': 'ACTIVAR MI MÁQUINA DE VENTAS',
    'hero.cta_secondary': 'Probar Chatbot AHORA',

    // Platform Features
    'feature.whatsapp.title': 'WhatsApp Automático',
    'feature.whatsapp.desc':
      'Cada lead recibe seguimiento personalizado 24/7 - sin que muevas un dedo',
    'feature.qualify.title': 'Califica Solo',
    'feature.qualify.desc': 'IA descubre presupuesto, urgencia e intención real de cada interesado',
    'feature.commission.title': 'Más Comisiones',
    'feature.commission.desc':
      'Corredores facturan 3x más con el mismo esfuerzo usando nuestro sistema',

    // Common
    'common.free': 'GRATIS',
    'common.popular': 'POPULAR',
    'common.premium': 'PREMIUM',
    'common.learn_more': 'Saber Más',
    'common.contact_sales': 'Contactar Ventas',
  },
};

// Formatação de moedas
const currencyConfig = {
  BRL: { symbol: 'R$', locale: 'pt-BR' },
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'de-DE' },
};

// Mapeamento idioma -> moeda padrão
const languageToCurrency: Record<Language, Currency> = {
  pt: 'BRL',
  en: 'USD',
  es: 'EUR',
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('pt');
  const [currency, setCurrency] = useState<Currency>('BRL');

  // Sincronizar moeda com idioma
  useEffect(() => {
    setCurrency(languageToCurrency[language]);
  }, [language]);

  // Função de tradução
  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)[typeof language]] || key;
  };

  // Formatação de preços
  const formatPrice = (price: number): string => {
    const config = currencyConfig[currency];

    // Conversão de preços (base BRL)
    let convertedPrice = price;
    if (currency === 'USD') {
      convertedPrice = price * 0.184; // BRL to USD
    } else if (currency === 'EUR') {
      convertedPrice = price * 0.169; // BRL to EUR
    }

    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
    }).format(convertedPrice);
  };

  const value = {
    language,
    currency,
    setLanguage,
    setCurrency,
    t,
    formatPrice,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
